import { useState, useEffect, useCallback, useMemo } from 'react'
import {
    Container, Grid, Paper, Typography, List, ListItem, ListItemText,
    Button, Divider, IconButton, Box, Alert, CircularProgress,
    TextField, Accordion, AccordionSummary, AccordionDetails,
    Chip, LinearProgress, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material'
import {
    ExpandMore, Refresh, People, Construction, Description,
    Sync, Storage, CheckCircle, ArrowForward, Delete, Send, DoneAll,
    Email, NotificationsActive, NotificationsOff, LocationOn, Person,
    Settings, Alarm, MenuBook, PictureAsPdf, Edit
} from '@mui/icons-material'
import { useSitesContext, useUser, useUsersContext } from '../context'
import { useStatus } from '../context/Status.context'
import { usersRoutes, machinesRoutes, reportsRoutes, sitesRoutes, syncRoutes, cronsRoutes, manualsRoutes } from '../routes'
import { GenerationStatusModal } from '../dialogs/StatusModal'
import { ROLES } from '../config'

const TestPage = () => {
    const { isSuperAdmin } = useUser()
    const { refreshSites, getAllSitesIncludingHidden, sites } = useSitesContext()
    const { users, getUsers, roles } = useUsersContext()
    const { setstatus } = useStatus()

    // Accordion States (Persisted in LocalStorage, closed by default)
    const [expanded, setExpanded] = useState(() => {
        const saved = localStorage.getItem('testPage_accordions_v2')
        return saved ? JSON.parse(saved) : { sync: false, migration: false, notifications: false, crons: false }
    })

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        const newState = { ...expanded, [panel]: isExpanded }
        setExpanded(newState)
        localStorage.setItem('testPage_accordions_v2', JSON.stringify(newState))
    }

    // Migration States
    const [hiddenSites, setHiddenSites] = useState([])
    const [activeSites, setActiveSites] = useState([])
    const [mappings, setMappings] = useState([])
    const [selectedHidden, setSelectedHidden] = useState(null)
    const [selectedActive, setSelectedActive] = useState(null)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [searchHidden, setSearchHidden] = useState('')
    const [searchActive, setSearchActive] = useState('')

    // Sync States
    const [syncStatus, setSyncStatus] = useState(null)
    const [lastFetch, setLastFetch] = useState(null)

    // Cron States
    const [allCrons, setAllCrons] = useState([])
    const [editingCrons, setEditingCrons] = useState({})

    // Notification States
    const [userSearch, setUserSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('all')
    const [showSubscribedOnly, setShowSubscribedOnly] = useState(false)

    // Manuals States
    const [adminManuals, setAdminManuals] = useState([])
    const [manualTitle, setManualTitle] = useState('')
    const [manualDescription, setManualDescription] = useState('')
    const [manualFile, setManualFile] = useState(null)
    const [selectedManualRoles, setSelectedManualRoles] = useState([])
    const [uploadingManual, setUploadingManual] = useState(false)
    const [overwriteModalOpen, setOverwriteModalOpen] = useState(false)
    const [conflictMessage, setConflictMessage] = useState('')

    const fetchAdminManuals = async () => {
        try {
            const res = await manualsRoutes.getAdminManuals()
            if (res && res.data) {
                setAdminManuals(res.data)
            }
        } catch (err) {
            console.error('Error fetching admin manuals:', err)
        }
    }

    const handleUploadManual = async (e, forceOverwrite = false) => {
        if (e && e.preventDefault) e.preventDefault()

        if (!manualFile) {
            setstatus({
                status: 'error',
                title: 'Archivo requerido',
                message: 'Por favor seleccione un archivo PDF para subir.',
                severity: 'warning'
            })
            return
        }

        const maxSizeBytes = 15 * 1024 * 1024 // 15MB
        if (manualFile.size > maxSizeBytes) {
            setstatus({
                status: 'error',
                title: 'Tamaño excedido',
                message: 'El archivo seleccionado supera el tamaño máximo permitido de 15 MB.',
                severity: 'error'
            })
            return
        }

        if (!manualTitle.trim() || !manualDescription.trim()) {
            setstatus({
                status: 'error',
                title: 'Campos requeridos',
                message: 'Debe ingresar un título y una descripción para el manual.',
                severity: 'warning'
            })
            return
        }

        setUploadingManual(true)
        setstatus({
            status: 'loading',
            title: forceOverwrite ? 'Sobrescribiendo Manual de Uso' : 'Subiendo Manual de Uso',
            message: 'Almacenando archivo en servidor Azure... Por favor espere.'
        })

        try {
            const formData = new FormData()
            formData.append('file', manualFile)
            formData.append('title', manualTitle.trim())
            formData.append('description', manualDescription.trim())
            formData.append('rolesAllowed', JSON.stringify(selectedManualRoles))
            if (forceOverwrite) {
                formData.append('overwrite', 'true')
            }

            const response = await manualsRoutes.uploadManual(formData)

            if (response.status === 201 || response.status === 200 || response.data) {
                setstatus({
                    status: 'completed',
                    title: forceOverwrite ? 'Manual Sobrescrito Exitosamente' : 'Manual Subido Exitosamente',
                    message: `El manual "${manualTitle}" fue cargado correctamente. Recuerde habilitarlo con el interruptor en la lista.`,
                    severity: 'success'
                })
                setManualTitle('')
                setManualDescription('')
                setManualFile(null)
                setSelectedManualRoles([])
                setOverwriteModalOpen(false)
                const fileInput = document.getElementById('manual-file-input')
                if (fileInput) fileInput.value = ''
                await fetchAdminManuals()
            }
        } catch (error) {
            console.error('Error uploading manual:', error)
            const httpStatus = error.response?.status
            if (httpStatus === 409) {
                const msg = error.response?.data?.message || 'Ya existe un manual almacenado con ese mismo nombre de archivo.'
                setConflictMessage(msg)
                setOverwriteModalOpen(true)
                setstatus({
                    status: 'error',
                    title: 'Documento Existente',
                    message: 'Ya existe un manual con ese nombre de archivo. Confirme si desea sobrescribirlo.',
                    severity: 'warning'
                })
            } else {
                const serverMessage = error.response?.data?.message
                const errorMsg = Array.isArray(serverMessage)
                    ? serverMessage.join(', ')
                    : (typeof serverMessage === 'string' ? serverMessage : (error.message || 'Error al subir el manual.'))
                setstatus({
                    status: 'error',
                    title: 'Error al Subir Manual',
                    message: 'No se pudo completar la subida del manual.',
                    errorDetails: {
                        code: httpStatus || '400',
                        info: errorMsg
                    },
                    severity: 'error'
                })
            }
        } finally {
            setUploadingManual(false)
        }
    }

    const handleToggleManualState = async (manualId, currentState) => {
        try {
            await manualsRoutes.toggleManualState(manualId, !currentState)
            await fetchAdminManuals()
        } catch (err) {
            console.error('Error toggling manual state:', err)
            setstatus({
                status: 'error',
                title: 'Error de Estado',
                message: 'No se pudo cambiar el estado del manual.',
                severity: 'error'
            })
        }
    }

    // Edit & Delete Manual States
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editingManual, setEditingManual] = useState(null)
    const [editTitle, setEditTitle] = useState('')
    const [editDescription, setEditDescription] = useState('')
    const [editFile, setEditFile] = useState(null)
    const [editRoles, setEditRoles] = useState([])
    const [updatingManual, setUpdatingManual] = useState(false)

    const handleOpenEditModal = (manual) => {
        setEditingManual(manual)
        setEditTitle(manual.title || '')
        setEditDescription(manual.description || '')
        setEditRoles((manual.rolesAllowed || []).map((r) => r._id || r))
        setEditFile(null)
        setEditModalOpen(true)
    }

    const handleSaveEditManual = async (e) => {
        if (e && e.preventDefault) e.preventDefault()
        if (!editingManual) return

        if (!editTitle.trim() || !editDescription.trim()) {
            setstatus({
                status: 'error',
                title: 'Campos requeridos',
                message: 'Debe ingresar un título y una descripción para el manual.',
                severity: 'warning'
            })
            return
        }

        if (editFile) {
            const maxSizeBytes = 15 * 1024 * 1024 // 15MB
            if (editFile.size > maxSizeBytes) {
                setstatus({
                    status: 'error',
                    title: 'Tamaño excedido',
                    message: 'El archivo seleccionado supera el tamaño máximo permitido de 15 MB.',
                    severity: 'error'
                })
                return
            }
        }

        setUpdatingManual(true)
        setstatus({
            status: 'loading',
            title: 'Actualizando Manual',
            message: 'Guardando los cambios en el servidor...'
        })

        try {
            const formData = new FormData()
            formData.append('title', editTitle.trim())
            formData.append('description', editDescription.trim())
            formData.append('rolesAllowed', JSON.stringify(editRoles))
            if (editFile) {
                formData.append('file', editFile)
            }

            await manualsRoutes.updateManual(editingManual._id, formData)

            setstatus({
                status: 'completed',
                title: 'Manual Actualizado',
                message: `El manual "${editTitle}" fue actualizado correctamente.`,
                severity: 'success'
            })
            setEditModalOpen(false)
            setEditingManual(null)
            await fetchAdminManuals()
        } catch (error) {
            console.error('Error updating manual:', error)
            const serverMessage = error.response?.data?.message
            const errorMsg = Array.isArray(serverMessage)
                ? serverMessage.join(', ')
                : (typeof serverMessage === 'string' ? serverMessage : (error.message || 'Error al actualizar el manual.'))
            setstatus({
                status: 'error',
                title: 'Error al Actualizar',
                message: 'No se pudieron guardar los cambios en el manual.',
                errorDetails: {
                    code: error.response?.status || '400',
                    info: errorMsg
                },
                severity: 'error'
            })
        } finally {
            setUpdatingManual(false)
        }
    }

    const handleDeleteManual = async (manual) => {
        if (!confirm(`¿Está seguro que desea eliminar el manual "${manual.title}"?`)) {
            return
        }

        setstatus({
            status: 'loading',
            title: 'Eliminando Manual',
            message: 'Eliminando el documento...'
        })

        try {
            await manualsRoutes.deleteManual(manual._id)
            setstatus({
                status: 'completed',
                title: 'Manual Eliminado',
                message: `El manual "${manual.title}" fue eliminado exitosamente.`,
                severity: 'success'
            })
            await fetchAdminManuals()
        } catch (error) {
            console.error('Error deleting manual:', error)
            const serverMessage = error.response?.data?.message
            const errorMsg = Array.isArray(serverMessage)
                ? serverMessage.join(', ')
                : (typeof serverMessage === 'string' ? serverMessage : (error.message || 'Error al eliminar el manual.'))
            setstatus({
                status: 'error',
                title: 'Error al Eliminar',
                message: 'No se pudo eliminar el manual.',
                errorDetails: {
                    code: error.response?.status || '400',
                    info: errorMsg
                },
                severity: 'error'
            })
        }
    }

    const loadData = async () => {
        setLoading(true)
        const allSites = await getAllSitesIncludingHidden()
        if (allSites) {
            setHiddenSites(allSites.filter(s => s.mostrar === false))
            setActiveSites(allSites.filter(s => s.mostrar === true))
        }
        await fetchCrons()
        await fetchAdminManuals()
        setLoading(false)
    }

    const fetchCrons = async () => {
        try {
            const res = await cronsRoutes.getAll()
            setAllCrons(res.data)
            // Initialize editing state
            const initialEditing = {}
            res.data.forEach(c => {
                initialEditing[c.name] = { ...c }
            })
            setEditingCrons(initialEditing)
        } catch (error) {
            console.error('Error fetching crons:', error)
        }
    }

    const handleCronEditChange = (name, field, value) => {
        setEditingCrons(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                [field]: value
            }
        }))
    }

    const handleSaveCron = async (name) => {
        setLoading(true)
        try {
            const data = editingCrons[name]
            await cronsRoutes.update(name, {
                time: data.time,
                repeatValue: parseInt(data.repeatValue),
                repeatUnit: data.repeatUnit,
                enabled: data.enabled
            })
            setstatus({
                status: 'completed',
                title: 'Cron Actualizado',
                message: `Configuración de '${name}' guardada correctamente.`,
                severity: 'success'
            })
            fetchCrons()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error al actualizar Cron',
                message: error.response?.data?.message || error.message,
                severity: 'error',
                errorDetails: {
                    code: error.response?.status || '500',
                    info: error.response?.data?.message || error.message
                }
            })
        } finally {
            setLoading(false)
        }
    }

    // Filter users who can receive emails (Exclude operators, must have email)
    const validNotificationUsers = useMemo(() => {
        return users.filter(u => {
            // 1. Basic requirements (Email + Not Operator)
            const hasEmail = u.email && typeof u.email === 'string' && u.email.trim() !== ''
            const roleObj = u.role
            const roleName = roleObj?.dbName || roleObj
            const isNotOperator = ![ROLES.OPERATOR, ROLES.INSPECTION_WORKER, ROLES.MAINTENANCE_OPERATOR].includes(roleName)

            if (!hasEmail || !isNotOperator) return false

            // 2. UI Filters
            const matchesRoleFilter = roleFilter === 'all' ||
                                     (typeof roleObj === 'object' ? roleObj._id === roleFilter : roleObj === roleFilter)
            if (!matchesRoleFilter) return false

            const matchesSubscription = !showSubscribedOnly || u.mailNotification === true
            if (!matchesSubscription) return false

            // 3. Search filter
            if (userSearch) {
                const search = userSearch.toLowerCase()
                const fullName = `${u.name || ''} ${u.lastName || ''}`.toLowerCase()
                const email = (u.email || '').toLowerCase()
                return fullName.includes(search) || email.includes(search)
            }

            return true
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }, [users, userSearch, roleFilter, showSubscribedOnly])

    const toggleNotification = async (user) => {
        setLoading(true)
        try {
            const newStatus = !user.mailNotification
            await usersRoutes.editUser({ mailNotification: newStatus }, user._id)
            setstatus({
                status: 'completed',
                title: 'Usuario Actualizado',
                message: `Notificaciones ${newStatus ? 'activadas' : 'desactivadas'} para ${user.name}`,
                severity: 'success'
            })
            await getUsers()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error al actualizar',
                message: error.message,
                severity: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    const fetchSyncStatus = useCallback(async () => {
        try {
            const response = await syncRoutes.getStatus()
            setSyncStatus(response.data)
            setLastFetch(new Date().toLocaleTimeString())
        } catch (error) {
            console.error('Error fetching sync status:', error)
        }
    }, [])

    useEffect(() => {
        if (!isSuperAdmin) return
        loadData()
        fetchSyncStatus()
    }, [isSuperAdmin, fetchSyncStatus])

    const handleManualRefresh = async () => {
        setLoading(true)
        await fetchSyncStatus()
        setLoading(false)
    }

    const handleSyncAll = async () => {
        if (!window.confirm('¿Desea iniciar la sincronización completa ahora?')) return
        setLoading(true)
        try {
            await syncRoutes.syncAll()
            setstatus({
                status: 'completed',
                title: 'Sincronización',
                message: 'Sincronización completa iniciada correctamente.',
                severity: 'success'
            })
            fetchSyncStatus()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error Sincronización',
                message: 'Error iniciando sincronización completa.',
                errorDetails: {
                    code: error.response?.status || '500',
                    info: error.response?.data?.message || error.message
                }
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSyncSites = async () => {
        if (!window.confirm('¿Desea iniciar la sincronización de sitios ahora?')) return
        setLoading(true)
        try {
            await syncRoutes.syncSites()
            setstatus({
                status: 'completed',
                title: 'Sincronización',
                message: 'Sincronización de sitios iniciada correctamente.',
                severity: 'success'
            })
            fetchSyncStatus()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error Sincronización',
                message: 'Error iniciando sincronización de sitios.',
                errorDetails: {
                    code: error.response?.status || '500',
                    info: error.response?.data?.message || error.message
                }
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSyncMachines = async () => {
        if (!window.confirm('¿Desea iniciar la sincronización de máquinas ahora?')) return
        setLoading(true)
        try {
            await syncRoutes.syncMachines()
            setstatus({
                status: 'completed',
                title: 'Sincronización',
                message: 'Sincronización de máquinas iniciada correctamente.',
                severity: 'success'
            })
            fetchSyncStatus()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error Sincronización',
                message: 'Error iniciando sincronización de máquinas.',
                errorDetails: {
                    code: error.response?.status || '500',
                    info: error.response?.data?.message || error.message
                }
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSyncReports = async () => {
        if (!window.confirm('¿Desea iniciar la sincronización de reportes ahora?')) return
        setLoading(true)
        try {
            await syncRoutes.syncReports()
            setstatus({
                status: 'completed',
                title: 'Sincronización',
                message: 'Sincronización de reportes iniciada correctamente.',
                severity: 'success'
            })
            fetchSyncStatus()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error Sincronización',
                message: 'Error iniciando sincronización de reportes.',
                errorDetails: {
                    code: error.response?.status || '500',
                    info: error.response?.data?.message || error.message
                }
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSyncPatterns = async () => {
        if (!window.confirm('¿Desea iniciar la sincronización de pautas ahora?')) return
        setLoading(true)
        try {
            await syncRoutes.syncPatterns()
            setstatus({
                status: 'completed',
                title: 'Sincronización',
                message: 'Sincronización de pautas iniciada correctamente.',
                severity: 'success'
            })
            fetchSyncStatus()
        } catch (error) {
            setstatus({
                status: 'error',
                title: 'Error Sincronización',
                message: 'Error iniciando sincronización de pautas.',
                errorDetails: {
                    code: error.response?.status || '500',
                    info: error.response?.data?.message || error.message
                }
            })
        } finally {
            setLoading(false)
        }
    }

    if (!isSuperAdmin) {
        return (
            <Container sx={{ mt: 5 }}>
                <Alert severity="error">Acceso denegado. Se requieren permisos de Super Administrador.</Alert>
            </Container>
        )
    }

    const availableHiddenSites = hiddenSites.filter(hs =>
        !mappings.some(m => m.from._id === hs._id) &&
        (hs.descripcion.toLowerCase().includes(searchHidden.toLowerCase()) || hs.idObra.includes(searchHidden))
    )

    const filteredActiveSites = activeSites.filter(as =>
        as.descripcion.toLowerCase().includes(searchActive.toLowerCase()) || as.idObra.includes(searchActive)
    )

    const addMapping = () => {
        if (selectedHidden && selectedActive) {
            if (mappings.find(m => m.from._id === selectedHidden._id)) {
                alert('Este sitio oculto ya tiene un mapeo asignado.')
                return
            }

            setMappings([...mappings, {
                from: selectedHidden,
                to: selectedActive,
                id: Date.now()
            }])
            setSelectedHidden(null)
            setSelectedActive(null)
        }
    }

    const removeMapping = (id) => {
        setMappings(mappings.filter(m => m.id !== id))
    }

    const handleMigration = async (type, mapping) => {
        setLoading(true)
        try {
            let response
            let message = ''

            switch (type) {
            case 'users': {
                response = await usersRoutes.migrateUsersToSite(mapping.from._id, mapping.to._id)
                message = `Usuarios migrados: ${response.data.modifiedCount || 0}`
                break
            }
            case 'machines':
                response = await machinesRoutes.migrateMachinesToSite(mapping.from.idObra, mapping.to.idObra, mapping.to.idObraAlfa)
                message = `Máquinas migradas: ${response.modifiedCount || 0}`
                break
            case 'reports':
                response = await reportsRoutes.migrateReportsToSite(mapping.from._id, mapping.to._id, mapping.to.idObra)
                message = `Reportes migrados: ${response.modifiedCount || 0}`
                break
            case 'all': {
                response = await sitesRoutes.migrateAllContent(mapping.from._id, mapping.to._id)
                const res = response.data.results
                message = `Migración completa: Usuarios(${res.users}), Máquinas(${res.machines}), Reportes(${res.reports})`
                break
            }
            default:
                break
            }

            setResult({
                type: 'success',
                message: `[${mapping.from.idObra} -> ${mapping.to.idObra}] ${message}`
            })

            if (type === 'all') {
                removeMapping(mapping.id)
            }
        } catch (error) {
            setResult({
                type: 'error',
                message: `Error migrando ${type}: ${error.message}`
            })
        } finally {
            setLoading(false)
        }
    }

    const migrateAllMappings = async () => {
        if (mappings.length === 0) return
        if (!window.confirm(`¿Ejecutar migración COMPLETA para ${mappings.length} sitios?`)) return

        setLoading(true)
        const total = { users: 0, machines: 0, reports: 0 }

        try {
            for (const mapping of mappings) {
                const response = await sitesRoutes.migrateAllContent(mapping.from._id, mapping.to._id)
                const res = response.data.results
                total.users += res.users
                total.machines += res.machines
                total.reports += res.reports
            }
            setResult({
                type: 'success',
                message: `Migración masiva terminada. Total: ${total.users} usuarios, ${total.machines} máquinas, ${total.reports} reportes.`
            })
            setMappings([])
            refreshSites()
        } catch (error) {
            setResult({ type: 'error', message: `Error en migración masiva: ${error.message}` })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" fontWeight="bold">Panel Maestro de Administración</Typography>
                <Chip
                    icon={<Sync />}
                    label={`Estado API: ${lastFetch || 'Consultando...'}`}
                    variant="outlined"
                    color="primary"
                />
            </Box>

            {result && (
                <Alert severity={result.type} sx={{ mb: 3 }} onClose={() => setResult(null)}>
                    {result.message}
                </Alert>
            )}

            {/* NOTIFICATION SECTION */}
            <Accordion
                expanded={expanded.notifications}
                onChange={handleAccordionChange('notifications')}
                sx={{ mb: 2, borderRadius: '8px !important', boxShadow: 2 }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Email color="primary" />
                        <Typography variant="h6">Gestión de Notificaciones de Correo</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Filtre por Rol para gestionar quién recibe los resúmenes de hallazgos. Se excluyen operarios por defecto.
                            </Alert>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Filtrar por Rol</InputLabel>
                                    <Select
                                        value={roleFilter}
                                        label="Filtrar por Rol"
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                        sx={{ bgcolor: 'white' }}
                                    >
                                        <MenuItem value="all">Todos los Roles (Habilitados)</MenuItem>
                                        {roles.filter(r => ![ROLES.OPERATOR, ROLES.INSPECTION_WORKER, ROLES.MAINTENANCE_OPERATOR].includes(r.dbName)).map(role => (
                                            <MenuItem key={role._id} value={role._id}>{role.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    size="small"
                                    placeholder="Buscar usuario o correo..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    sx={{ width: 300, bgcolor: 'white' }}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showSubscribedOnly}
                                            onChange={(e) => setShowSubscribedOnly(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={<Typography variant="body2">Ver solo suscritos</Typography>}
                                    sx={{ ml: 1 }}
                                />
                            </Paper>

                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Usuario</TableCell>
                                            <TableCell>Correo</TableCell>
                                            <TableCell>Rol</TableCell>
                                            <TableCell>Obras</TableCell>
                                            <TableCell align="center">Notificación</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {validNotificationUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                                    Seleccione un rol o ajuste la búsqueda.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            validNotificationUsers.map(user => {
                                                const uSites = user.obras?.map(o => {
                                                    const sId = (typeof o === 'object') ? (o._id || o.id) : o
                                                    return sites.find(s => s._id === sId)?.idObra || '?'
                                                }).join(', ') || 'N/A'

                                                return (
                                                    <TableRow key={user._id} hover>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Person fontSize="small" color="action" />
                                                                <Typography variant="body2" fontWeight="medium">{user.name} {user.lastName}</Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>{user.email}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>{user.role?.name || user.role}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {uSites}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Switch
                                                                size="small"
                                                                checked={user.mailNotification || false}
                                                                onChange={() => toggleNotification(user)}
                                                                disabled={loading}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* SYNC STATUS ACCORDION */}
            <Accordion
                expanded={expanded.sync}
                onChange={handleAccordionChange('sync')}
                sx={{ mb: 2, borderRadius: '8px !important', boxShadow: 2 }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Sync color="primary" />
                        <Typography variant="h6">Estado de Sincronización (Real-time)</Typography>
                        {syncStatus?.current?.isSyncing && (
                            <Chip label="ACTIVO" color="warning" size="small" />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: '#f5f5f5', p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={7}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Progreso Actual</Typography>
                                {!syncStatus?.current?.isSyncing ? (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography color="textSecondary">No hay procesos de sincronización en curso.</Typography>
                                        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<Sync />}
                                                onClick={handleSyncAll}
                                                disabled={loading}
                                            >
                                                Sincronizar Todo
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<Sync />}
                                                onClick={handleSyncSites}
                                                disabled={loading}
                                            >
                                                Sincronizar Sitios
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<Sync />}
                                                onClick={handleSyncMachines}
                                                disabled={loading}
                                            >
                                                Sincronizar Máquinas
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<Sync />}
                                                onClick={handleSyncReports}
                                                disabled={loading}
                                            >
                                                Sincronizar Reportes
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<Sync />}
                                                onClick={handleSyncPatterns}
                                                disabled={loading}
                                            >
                                                Sincronizar Pautas
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                            Acción: <strong>{syncStatus.current.currentAction}</strong>
                                        </Typography>
                                        <LinearProgress variant="indeterminate" sx={{ mb: 3, height: 10, borderRadius: 5 }} />
                                        <Grid container spacing={2}>
                                            {Object.entries(syncStatus.current.progress || {}).map(([key, val]) => (
                                                <Grid item xs={6} md={3} key={key}>
                                                    <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>{key}</Typography>
                                                        <Typography variant="h5">{val.processed || 0}/{val.total || '?'}</Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                                                            <Typography variant="caption" color="success.main">+{val.created || 0}</Typography>
                                                            <Typography variant="caption" color="primary.main">~{val.updated || 0}</Typography>
                                                            <Typography variant="caption" color="error.main">!{val.failed || 0}</Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Últimos Logs</Typography>
                                <List dense>
                                    {syncStatus?.history?.map((log) => (
                                        <ListItem key={log._id} divider sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {new Date(log.startTime).toLocaleString()}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={log.status}
                                                    color={log.status === 'success' ? 'success' : 'warning'}
                                                    variant="outlined"
                                                />
                                            </Box>

                                            <Grid container spacing={1}>
                                                {['machines', 'patterns', 'reports', 'sites', 'units'].map(category => (
                                                    log[category] && (
                                                        <Grid item xs={12} key={category}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="caption" sx={{ minWidth: 70, fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                                    {category}:
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <Typography variant="caption" color="success.main">+{log[category].created || 0}</Typography>
                                                                    <Typography variant="caption" color="primary.main">~{log[category].updated || 0}</Typography>
                                                                    {log[category].failed > 0 && <Typography variant="caption" color="error.main">!{log[category].failed}</Typography>}
                                                                    {log[category].skipped > 0 && <Typography variant="caption" color="text.disabled">({log[category].skipped} skp)</Typography>}
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    )
                                                ))}
                                            </Grid>
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* CRONS CONFIGURATION ACCORDION */}
            <Accordion
                expanded={expanded.crons}
                onChange={handleAccordionChange('crons')}
                sx={{ mb: 2, borderRadius: '8px !important', boxShadow: 2 }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Alarm color="primary" />
                        <Typography variant="h6">Configuración de Tareas Programadas (Crons)</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Configure el horario de inicio y la frecuencia de repetición para las tareas automáticas del sistema. IMPORTANTE: Cada cron activo debe tener un desfase de al menos 10 minutos entre sí (por ejemplo, evitar que coincidan o se topen con 'sync_all' u otros).
                            </Alert>
                        </Grid>
                        {allCrons.map((cron) => {
                            const editData = editingCrons[cron.name] || cron
                            return (
                                <Grid item xs={12} md={6} lg={4} key={cron.name}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdfdfd' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                {cron.name}
                                            </Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={editData.enabled}
                                                        onChange={(e) => handleCronEditChange(cron.name, 'enabled', e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label={<Typography variant="caption">Activo</Typography>}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, minHeight: 32 }}>
                                            {cron.description}
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Hora Inicio"
                                                    type="time"
                                                    size="small"
                                                    fullWidth
                                                    value={editData.time}
                                                    onChange={(e) => handleCronEditChange(cron.name, 'time', e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={7} sm={4}>
                                                <TextField
                                                    label="Cada"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={editData.repeatValue || 0}
                                                    onChange={(e) => handleCronEditChange(cron.name, 'repeatValue', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={5} sm={4}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Unidad</InputLabel>
                                                    <Select
                                                        value={editData.repeatUnit || 'hours'}
                                                        label="Unidad"
                                                        onChange={(e) => handleCronEditChange(cron.name, 'repeatUnit', e.target.value)}
                                                    >
                                                        <MenuItem value="hours">Horas</MenuItem>
                                                        <MenuItem value="days">Días</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => handleSaveCron(cron.name)}
                                                disabled={loading}
                                                startIcon={<Settings />}
                                            >
                                                Guardar
                                            </Button>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )
                        })}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* MIGRATION ACCORDION */}
            <Accordion
                expanded={expanded.migration}
                onChange={handleAccordionChange('migration')}
                sx={{ borderRadius: '8px !important', boxShadow: 2 }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Storage color="error" />
                        <Typography variant="h6">Consolidación de Sitios (Migración de Datos)</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        {/* Columna Sitios Ocultos */}
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 2, height: '500px', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" color="error" gutterBottom>Origen (Ocultos)</Typography>
                                <TextField
                                    size="small"
                                    placeholder="Buscar..."
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    value={searchHidden}
                                    onChange={(e) => setSearchHidden(e.target.value)}
                                />
                                <List dense sx={{ flexGrow: 1, overflow: 'auto' }}>
                                    {availableHiddenSites.map((site) => (
                                        <ListItem
                                            button
                                            key={site._id}
                                            selected={selectedHidden?._id === site._id}
                                            onClick={() => setSelectedHidden(site)}
                                            sx={{ borderRadius: 1, mb: 0.5 }}
                                        >
                                            <ListItemText
                                                primary={site.descripcion}
                                                secondary={`ID: ${site.idObra} | Alfa: ${site.idObraAlfa || 'N/A'}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>

                        {/* Botón Mapear */}
                        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <IconButton
                                color="primary"
                                disabled={!selectedHidden || !selectedActive}
                                onClick={addMapping}
                                size="large"
                            >
                                <ArrowForward fontSize="large" />
                            </IconButton>
                        </Grid>

                        {/* Columna Sitios Activos */}
                        <Grid item xs={12} md={3}>
                            <Paper sx={{ p: 2, height: '500px', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" color="primary" gutterBottom>Destino (Activos)</Typography>
                                <TextField
                                    size="small"
                                    placeholder="Buscar..."
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    value={searchActive}
                                    onChange={(e) => setSearchActive(e.target.value)}
                                />
                                <List dense sx={{ flexGrow: 1, overflow: 'auto' }}>
                                    {filteredActiveSites.map((site) => (
                                        <ListItem
                                            button
                                            key={site._id}
                                            selected={selectedActive?._id === site._id}
                                            onClick={() => setSelectedActive(site)}
                                            sx={{ borderRadius: 1, mb: 0.5 }}
                                        >
                                            <ListItemText
                                                primary={site.descripcion}
                                                secondary={`ID: ${site.idObra} | Alfa: ${site.idObraAlfa || 'N/A'}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>

                        {/* Panel de Control */}
                        <Grid item xs={12} md={5}>
                            <Paper sx={{ p: 2, height: '500px', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" gutterBottom>Control de Migración</Typography>
                                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                                    {mappings.length === 0 ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <Typography color="textSecondary">Sin mapeos</Typography>
                                        </Box>
                                    ) : (
                                        mappings.map((m) => (
                                            <Paper variant="outlined" key={m.id} sx={{ p: 2, mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="error">DE: {m.from.descripcion}</Typography>
                                                    <IconButton size="small" onClick={() => removeMapping(m.id)}><Delete /></IconButton>
                                                </Box>
                                                <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>A: {m.to.descripcion}</Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}><Button fullWidth size="small" variant="outlined" startIcon={<People />} onClick={() => handleMigration('users', m)} disabled={loading}>Usuarios</Button></Grid>
                                                    <Grid item xs={6}><Button fullWidth size="small" variant="outlined" startIcon={<Construction />} onClick={() => handleMigration('machines', m)} disabled={loading}>Máquinas</Button></Grid>
                                                    <Grid item xs={6}><Button fullWidth size="small" variant="outlined" startIcon={<Description />} onClick={() => handleMigration('reports', m)} disabled={loading}>Reportes</Button></Grid>
                                                    <Grid item xs={6}><Button fullWidth size="small" variant="contained" color="warning" startIcon={<DoneAll />} onClick={() => handleMigration('all', m)} disabled={loading}>TODO</Button></Grid>
                                                </Grid>
                                            </Paper>
                                        ))
                                    )}
                                </Box>
                                {mappings.length > 0 && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        fullWidth
                                        onClick={migrateAllMappings}
                                        disabled={loading}
                                        sx={{ mt: 2 }}
                                        startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <Send />}
                                    >
                                        EJECUTAR LISTA
                                    </Button>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* ACCORDION 5: SUBIDA Y GESTIÓN DE MANUALES */}
            <Accordion expanded={expanded.manuals || false} onChange={handleAccordionChange('manuals')} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MenuBook color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Subida y Gestión de Manuales de Uso</Typography>
                        <Chip label="Subidas PDF (Máx 15MB)" size="small" color="info" sx={{ ml: 1 }} />
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        {/* FORMULARIO DE SUBIDA */}
                        <Grid item xs={12} md={5}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Subir Nuevo Manual (PDF)
                                </Typography>
                                <form onSubmit={handleUploadManual}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                            Documento PDF (Máximo 15 MB) *
                                        </Typography>
                                        <input
                                            id="manual-file-input"
                                            type="file"
                                            accept="application/pdf"
                                            onChange={(e) => setManualFile(e.target.files[0] || null)}
                                            style={{ width: '100%' }}
                                        />
                                        {manualFile && (
                                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                                Archivo seleccionado: {manualFile.name} ({(manualFile.size / (1024 * 1024)).toFixed(2)} MB)
                                            </Typography>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Título del Documento *"
                                        value={manualTitle}
                                        onChange={(e) => setManualTitle(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        label="Descripción *"
                                        value={manualDescription}
                                        onChange={(e) => setManualDescription(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />

                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                        <InputLabel id="roles-allowed-label">Roles Autorizados</InputLabel>
                                        <Select
                                            labelId="roles-allowed-label"
                                            multiple
                                            value={selectedManualRoles}
                                            onChange={(e) => setSelectedManualRoles(e.target.value)}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => {
                                                        const roleObj = roles.find((r) => r._id === value)
                                                        return <Chip key={value} label={roleObj ? roleObj.name : value} size="small" />
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {roles.map((role) => (
                                                <MenuItem key={role._id} value={role._id}>
                                                    {role.name} ({role.dbName})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={uploadingManual}
                                        startIcon={uploadingManual ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                                    >
                                        Subir Manual
                                    </Button>
                                </form>
                            </Paper>
                        </Grid>

                        {/* LISTA DE MANUALES ALMACENADOS */}
                        <Grid item xs={12} md={7}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', minHeight: 350 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        Manuales Almacenados ({adminManuals.length})
                                    </Typography>
                                    <IconButton size="small" onClick={fetchAdminManuals} title="Refrescar manuales">
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Box>
                                {adminManuals.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 4 }}>
                                        No hay manuales cargados en el sistema.
                                    </Typography>
                                ) : (
                                    <TableContainer sx={{ maxHeight: 350 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Título / Descripción</strong></TableCell>
                                                    <TableCell><strong>Roles</strong></TableCell>
                                                    <TableCell align="center"><strong>Estado</strong></TableCell>
                                                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {adminManuals.map((m) => (
                                                    <TableRow key={m._id}>
                                                        <TableCell>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{m.title}</Typography>
                                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{m.description}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {m.rolesAllowed && m.rolesAllowed.length > 0 ? (
                                                                    m.rolesAllowed.map((r) => (
                                                                        <Chip key={r._id || r} label={r.name || r} size="small" variant="outlined" />
                                                                    ))
                                                                ) : (
                                                                    <Chip label="Todos" size="small" color="default" />
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={!!m.state}
                                                                        onChange={() => handleToggleManualState(m._id, m.state)}
                                                                        size="small"
                                                                        color="success"
                                                                    />
                                                                }
                                                                label={m.state ? 'Activo' : 'Inactivo'}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                title="Editar manual"
                                                                onClick={() => handleOpenEditModal(m)}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                title="Eliminar manual"
                                                                onClick={() => handleDeleteManual(m)}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* MODAL DE EDICIÓN DE MANUAL */}
            <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Editar Manual de Uso
                </DialogTitle>
                <form onSubmit={handleSaveEditManual}>
                    <DialogContent dividers>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                Reemplazar Documento PDF (Opcional - Máx 15 MB)
                            </Typography>
                            <input
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => setEditFile(e.target.files[0] || null)}
                                style={{ width: '100%' }}
                            />
                            {editFile && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Nuevo archivo seleccionado: {editFile.name} ({(editFile.size / (1024 * 1024)).toFixed(2)} MB)
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            fullWidth
                            size="small"
                            label="Título del Documento *"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            label="Descripción *"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel id="edit-roles-allowed-label">Roles Autorizados</InputLabel>
                            <Select
                                labelId="edit-roles-allowed-label"
                                multiple
                                value={editRoles}
                                onChange={(e) => setEditRoles(e.target.value)}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const roleObj = roles.find((r) => r._id === value)
                                            return <Chip key={value} label={roleObj ? roleObj.name : value} size="small" />
                                        })}
                                    </Box>
                                )}
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role._id} value={role._id}>
                                        {role.name} ({role.dbName})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setEditModalOpen(false)} color="inherit">
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={updatingManual}
                            startIcon={updatingManual ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            Guardar Cambios
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Box sx={{ mt: 3 }}>
                <Button startIcon={<Refresh />} onClick={() => { loadData(); fetchSyncStatus() }} disabled={loading}>
                    Refrescar Todo
                </Button>
            </Box>
            {/* MODAL DE CONFIRMACIÓN PARA SOBREESCRIBIR ARCHIVO */}
            <Dialog open={overwriteModalOpen} onClose={() => setOverwriteModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    Documento Ya Existente
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#333' }}>
                        {conflictMessage || 'Ya existe un manual almacenado con ese mismo nombre de archivo. ¿Desea sobrescribirlo con la nueva versión?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => setOverwriteModalOpen(false)}
                        variant="outlined"
                        color="inherit"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => handleUploadManual(null, true)}
                        variant="contained"
                        color="error"
                    >
                        Sobrescribir / Subir
                    </Button>
                </DialogActions>
            </Dialog>

            <GenerationStatusModal />
        </Container>
    )
}

export default TestPage
